import React, { useState } from 'react'
import { Box, Card, Grid, Link, Typography, useMediaQuery, Button, Drawer } from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import { connect } from 'react-redux'
import { makeStyles } from '@material-ui/core/styles'
import cn from 'classnames'
import { Link as RouterLink } from 'react-router-dom'
import {
  selectCurrentLocationWithAddress,
  selectCurrentSavedLocation,
} from '../../redux/modules/selectors'
import { savedLocationsRoute } from '../../routes/routes'
import { formatAsMonthDayOrWeekday } from '../../util/dateUtils'
import { selectDateStart } from '../../redux/modules/delivery'
import { themeConWeb } from '../../util/modernThemeConweb'
import DatePickerWidget from './DatePickerWidget'

const useStyles = makeStyles(theme => ({
  addressBox: {
    alignItems: 'flex-start',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    padding: theme.spacing(2),
  },
  locationBox: { marginBottom: '25px' },
  locationTitle: {
    fontWeight: 800,
    fontSize: `${themeConWeb.fontSizes.basePlus1}`,
    lineHeight: '24px',
  },
  firstAddressLine: {
    fontSize: `${themeConWeb.fontSizes.base}`,
    fontWeight: 400,
    paddingBottom: '3px',
    paddingTp: '3px',
  },
  addressLine: {
    fontSize: `${themeConWeb.fontSizes.baseMinus1}`,
    marginTop: '2px',
  },
  changeSavedLocation: {
    cursor: 'pointer',
    fontWeight: 800,
    color: `${themeConWeb.color.two}`,
    '&:hover': {
      textDecoration: `underline ${themeConWeb.color.twoTint3}`,
    },
  },
  closeIcon: {
    justifyContent: 'flex-end',
    paddingBottom: '10px',
  },
  mobileAddress: {
    textAlign: 'left',
    paddingBottom: '5px',
  },
  locationAndDate: { textAlign: 'left' },
  viewEditButton: { color: `${themeConWeb.color.two}` },
  changeButton: {
    color: `${themeConWeb.color.danger}`,
    textAlign: 'left',
    fontSize: `${themeConWeb.fontSizes.baseMinus1}`,
    fontWeight: 800,
  },
  fullScreenDrawer: {
    width: '100vw',
    height: '100vh',
    overflow: 'auto',
    padding: '5%',
  },
  [theme.breakpoints.up('sm')]: {
    addressBox: {
      alignItems: 'center',
      flexDirection: 'row',
      padding: '10px',
    },
    firstAddressLine: {
      fontSize: `${themeConWeb.fontSizes.base}`,
      fontWeight: 400,
    },
    changeSavedLocation: {
      fontSize: `${themeConWeb.fontSizes.baseMinus1}`,
      margin: '12px auto',
    },
  },
  [theme.breakpoints.up('md')]: {
    addressBox: {
      alignItems: 'flex-start',
      flexDirection: 'column',
    },
    changeSavedLocation: {
      fontSize: `${themeConWeb.fontSizes.baseMinus1}`,
      margin: '12px 0',
    },
  },
}))

export const fillAddressLines = address => {
  let { name, deliveryLocationName, street, city } = address
  return [name || deliveryLocationName, `${street}, ${city}`].filter(Boolean)
}

const HomeSidebar = ({
  location,
  currentSavedLocation,
  selectDateStart,
  showCateringMobile,
  selectedDate,
}) => {
  const classes = useStyles()
  const mobileView = useMediaQuery(theme => theme.breakpoints.between('xs', 'sm'))
  const locationId = location.deliveryLocationId
  const [open, setOpen] = useState(false)
  const handleOpen = () => {
    setOpen(true)
  }

  const handleClose = () => {
    setOpen(false)
  }

  if (mobileView) {
    return (
      <HomeSidebarMobileView
        location={location}
        currentSavedLocation={currentSavedLocation}
        showCateringMobile={showCateringMobile}
        selectDateStart={selectDateStart}
        selectedDate={selectedDate}
        locationId={locationId}
        open={open}
        handleOpen={handleOpen}
        handleClose={handleClose}
        classes={classes}
      />
    )
  }

  return (
    <Grid container style={{ flexDirection: 'column' }}>
      <Grid item className={classes.locationBox}>
        <LocationWidget
          classes={classes}
          currentSavedLocation={currentSavedLocation}
          location={location}
        />
      </Grid>
      <Grid item>
        <DatePickerWidget
          selectDateStart={selectDateStart}
          locationId={locationId}
          selectedDate={selectedDate}
        />
      </Grid>
    </Grid>
  )
}

const LocationWidget = ({ classes, currentSavedLocation, location }) => (
  <>
    <Typography className={classes.locationTitle}>Location</Typography>
    <Box>
      {location &&
        fillAddressLines(currentSavedLocation || location).map((line, i) => {
          return (
            <>
              <Typography
                key={`${line}-${i}`}
                className={cn(classes.addressLine, { [classes.firstAddressLine]: i === 0 })}
              >
                {line}
              </Typography>
            </>
          )
        })}
    </Box>
    <span className={classes.changeSavedLocation}>
      <Link href={savedLocationsRoute.path}>Change</Link>
    </span>
  </>
)

const HomeSidebarMobileView = ({
  location,
  currentSavedLocation,
  selectDateStart,
  selectedDate,
  locationId,
  open,
  handleClose,
  handleOpen,
  classes,
}) => (
  <Card style={{ padding: '15px' }}>
    <Grid
      container
      spacing={1}
      style={{
        display: 'flex',
        justifyContent: 'center',
        marginTop: '10px',
        flexDirection: 'column',
        textAlign: 'center',
      }}
    >
      <Grid container justifyContent="space-between">
        <Grid item className={classes.locationAndDate}>
          <Grid>
            <Typography>
              <span style={{ fontWeight: 800 }}>Location: </span>
              {currentSavedLocation?.deliveryLocationName || location.deliveryLocationName}
            </Typography>
          </Grid>

          <Grid>
            <Typography>
              <span style={{ fontWeight: 800 }}>Date: </span>{' '}
              {formatAsMonthDayOrWeekday(selectedDate)}
            </Typography>
          </Grid>
        </Grid>
        <Grid item>
          <Button onClick={handleOpen} className={classes.viewEditButton}>
            View/Edit
          </Button>
        </Grid>
      </Grid>

      <Drawer open={open} anchor="top" classes={{ paper: classes.fullScreenDrawer }}>
        <Button className={classes.closeIcon} onClick={e => handleClose(e)}>
          <CloseIcon />
        </Button>
        <Typography variant="subtitle1">Delivery Details</Typography>
        <Typography className={classes.locationTitle}>Location</Typography>
        <Box display="flex" justifyContent="column" width="100%" flexDirection={'column'}>
          <Box className={classes.mobileAddress}>
            {location &&
              fillAddressLines(currentSavedLocation || location).map((line, i) => {
                return (
                  <>
                    <Typography
                      key={`${line}-${i}`}
                      className={cn(classes.addressLine, { [classes.firstAddressLine]: i === 0 })}
                    >
                      {line}
                    </Typography>
                  </>
                )
              })}
          </Box>
          <Link
            className={classes.changeButton}
            component={RouterLink}
            to={savedLocationsRoute.path}
          >
            Change
          </Link>
        </Box>
        <Box mt={3}></Box>
        <DatePickerWidget
          selectDateStart={selectDateStart}
          selectedDate={selectedDate}
          locationId={locationId}
        />
      </Drawer>
    </Grid>
  </Card>
)

const mapStateToProps = state => {
  const location = selectCurrentLocationWithAddress(state)
  const currentSavedLocation = selectCurrentSavedLocation(state)
  const { selectedDate } = state.delivery

  return {
    currentSavedLocation,
    location,
    selectedDate,
  }
}

const mapDispatchToProps = {
  selectDateStart,
}

export default connect(mapStateToProps, mapDispatchToProps)(HomeSidebar)
